import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { getDependencyId } from '../map_dependencies/utils/dependencyUtils';

export type ObjectiveDisplayMode = 'show_all' | 'show_with_dependencies' | 'show_all_relatives';

interface RelativesViewSettings {
  // View mode for relatives view. User updates these via Map Options Dropdown Menu in map_dependencies.
  viewMode: 'stacked' | 'sharedTimeline';
  // Objective display mode for controlling objective visibility
  objectiveDisplayMode: ObjectiveDisplayMode;
  // Maps manually added by the user
  addedMaps: Array<{
    mapUuid: string;
    visible: boolean;
  }>;
  // Maps that are related to currently visible maps as determined by dependencies
  relatedToVisibleMaps: Array<{
    mapUuid: string;
    visible: boolean;
  }>;
  // Dependencies between visible maps
  visibleMapsDependencies: Array<{
    depId: string;
    visible: boolean;
  }>;
}

interface MapControlsState {
  enableTreeView: boolean;
  enableSidebarTreeView: boolean;
  enableCrosshairs: boolean;
  enableRelativesView: boolean;
  enableImplementationPlansView: boolean;
  enableSettingsView: boolean;
  enablePlaylistMode: boolean;
  enableHierarchyZoomMode: boolean;
  enableHelpDialog: boolean;
  showStatusCodes: boolean;
  enableAddExistingObjectives: boolean;
  toggleStatusCellDisplay:
    | 'status'
    | 'status_results'
    | 'status_results_comments'
    | 'status_results2_comments';
  // Current zoom level for timeline views (null = use map's base interval)
  zoomLevel: MilestoneMapInterval | null;
  // UI settings specific to relatives view provided by components in map_dependencies.
  // Note re. Terminology: "Dependencies" are a type of relative. Parents, children, ancestors, and decendents are also relatives.
  // Relatives is used here as a generic catch-all for all types of relationships.
  relatives: RelativesViewSettings;
  // Deprecated.
  viewSettings: {
    // Settings specific React Flow views
    // (dependencies, relatives, etc.)
    dependencies?: {
      showGrid: boolean;
      showDebugNodes: boolean;
    };
    [key: string]: any;
  };
}

interface Map3UIState {
  mapControls: MapControlsState;
  drawerIsOpen: boolean;
  playlistDialogIsOpen: boolean;
  drawerTab: string;
  placeholderRows: number[];
  uri: string;
  searchQuery: {
    parents: string;
    children: string;
  };
  activePlaylist: Array<{ url: string; commentText: string }>;
  activePlaylistTitle: string;
  activePlaylistDescription: string;
  /** UUID of the human whose allocations are currently highlighted, or null if none */
  highlightedHumanUuid: string | null;
  // UUID of the currently highlighted objective, or null if none
  highlightedObjectiveUuid: string | null;
  // The forIntervalEnding of the currently highlighted interval column, or null if none
  // Used by the Status tab crosshairs effect to highlight both column and row
  highlightedIntervalEnding: string | null;
  // UUID of the currently highlighted result, or null if none
  // Set when the user activates highlight while viewing a result in the drawer
  highlightedResultUuid: string | null;
}

const initialState: Map3UIState = {
  mapControls: {
    enableTreeView: false,
    enableSidebarTreeView: false,
    enableCrosshairs: false,
    enableRelativesView: false,
    enableImplementationPlansView: false,
    enableSettingsView: false,
    enablePlaylistMode: false,
    enableHierarchyZoomMode: false,
    enableHelpDialog: false,
    showStatusCodes: false,
    enableAddExistingObjectives: false,
    toggleStatusCellDisplay: 'status_results2_comments',
    zoomLevel: null,
    relatives: {
      viewMode: 'sharedTimeline',
      objectiveDisplayMode: 'show_all',
      addedMaps: [],
      relatedToVisibleMaps: [],
      visibleMapsDependencies: [],
    },
    viewSettings: {},
  },
  drawerIsOpen: false,
  playlistDialogIsOpen: false,
  drawerTab: '',
  placeholderRows: [],
  uri: '',
  searchQuery: {
    parents: '',
    children: '',
  },
  activePlaylist: [],
  activePlaylistTitle: '',
  activePlaylistDescription: '',
  highlightedHumanUuid: null,
  highlightedObjectiveUuid: null,
  highlightedIntervalEnding: null,
  highlightedResultUuid: null,
};

export const map3UISlice = createSlice({
  name: 'map3UI',
  initialState,
  reducers: {
    setTreeView: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableTreeView = action.payload;
    },
    setSidebarTreeView: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableSidebarTreeView = action.payload;
    },
    setCrosshairs: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableCrosshairs = action.payload;
    },
    setRelativesView: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableRelativesView = action.payload;
    },
    setImplementationPlansView: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableImplementationPlansView = action.payload;
    },
    setSettingsView: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableSettingsView = action.payload;
    },
    setPlaylistMode: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enablePlaylistMode = action.payload;
    },
    setHierarchyZoomMode: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableHierarchyZoomMode = action.payload;
    },
    setHelpDialog: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableHelpDialog = action.payload;
    },
    setShowStatusCodes: (state, action: PayloadAction<boolean>) => {
      state.mapControls.showStatusCodes = action.payload;
    },
    setAddExistingObjectives: (state, action: PayloadAction<boolean>) => {
      state.mapControls.enableAddExistingObjectives = action.payload;
    },
    setStatusCellDisplay: (
      state,
      action: PayloadAction<
        'status' | 'status_results' | 'status_results_comments' | 'status_results2_comments'
      >,
    ) => {
      state.mapControls.toggleStatusCellDisplay = action.payload;
    },
    setZoomLevel: (state, action: PayloadAction<MilestoneMapInterval | null>) => {
      state.mapControls.zoomLevel = action.payload;
    },
    resetMapZoomLevel: (state) => {
      state.mapControls.zoomLevel = null;
    },
    setRelativesViewMode: (state, action: PayloadAction<'stacked' | 'sharedTimeline'>) => {
      state.mapControls.relatives.viewMode = action.payload;
    },
    setObjectiveDisplayMode: (state, action: PayloadAction<ObjectiveDisplayMode>) => {
      state.mapControls.relatives.objectiveDisplayMode = action.payload;
    },
    setAddedMaps: (state, action: PayloadAction<Array<{ mapUuid: string; visible: boolean }>>) => {
      state.mapControls.relatives.addedMaps = action.payload;
    },
    setRelatedToVisibleMaps: (
      state,
      action: PayloadAction<Array<{ mapUuid: string; visible: boolean }>>,
    ) => {
      state.mapControls.relatives.relatedToVisibleMaps = action.payload;
    },
    setVisibleMapsDependencies: (
      state,
      action: PayloadAction<Array<{ depId: string; visible: boolean }>>,
    ) => {
      state.mapControls.relatives.visibleMapsDependencies = action.payload;
    },
    // New action for bulk toggling all dependencies for a map
    toggleMapDependencies: (
      state,
      action: PayloadAction<{ mapUuid: string; dependencies: any[]; targetVisible: boolean }>,
    ) => {
      const { mapUuid, dependencies, targetVisible } = action.payload;

      // Find all dependencies for this map
      const mapDependencies = dependencies.filter(
        (dep) => dep.sourceMap.uuid === mapUuid || dep.targetMap.uuid === mapUuid,
      );

      // Update visibility for all dependencies of this map
      mapDependencies.forEach((dep) => {
        const depId = getDependencyId(dep);
        const existingDep = state.mapControls.relatives.visibleMapsDependencies.find(
          (d) => d.depId === depId,
        );
        if (existingDep) {
          existingDep.visible = targetVisible;
        }
      });

      // When making dependencies visible, also make the corresponding maps visible
      if (targetVisible) {
        const mapsToMakeVisible = new Set<string>();

        mapDependencies.forEach((dep) => {
          // Collect all maps that need to be visible for these dependencies to show
          mapsToMakeVisible.add(dep.sourceMap.uuid);
          mapsToMakeVisible.add(dep.targetMap.uuid);
        });

        // Make the maps visible in both addedMaps and relatedToVisibleMaps
        mapsToMakeVisible.forEach((mapUuidToShow) => {
          // Check addedMaps first
          const addedMapIndex = state.mapControls.relatives.addedMaps.findIndex(
            (m) => m.mapUuid === mapUuidToShow,
          );
          if (addedMapIndex !== -1) {
            state.mapControls.relatives.addedMaps[addedMapIndex].visible = true;
          }

          // Check relatedToVisibleMaps
          const relatedMapIndex = state.mapControls.relatives.relatedToVisibleMaps.findIndex(
            (m) => m.mapUuid === mapUuidToShow,
          );
          if (relatedMapIndex !== -1) {
            state.mapControls.relatives.relatedToVisibleMaps[relatedMapIndex].visible = true;
          }
        });
      }
    },

    toggleDrawer(state, action: PayloadAction<any>) {
      state.drawerIsOpen = !state.drawerIsOpen;
    },
    setPlaylistDialogOpen(state, action: PayloadAction<boolean>) {
      state.playlistDialogIsOpen = action.payload;
    },
    insertPlaceholderRow(state, action: PayloadAction<{ afterRow: number }>) {
      const newRow = action.payload.afterRow + 1;
      if (!state.placeholderRows.includes(newRow)) {
        state.placeholderRows = [...state.placeholderRows, newRow].sort((a, b) => a - b);
      }
    },
    removePlaceholderRow(state, action: PayloadAction<{ row: number }>) {
      if (action.payload.row === -1) {
        // Special case: clear all placeholders
        state.placeholderRows = [];
      } else {
        state.placeholderRows = state.placeholderRows.filter((r) => r !== action.payload.row);
      }
    },
    setUri(state, action: PayloadAction<string>) {
      const uri = action.payload;
      state.uri = uri;
      state.drawerIsOpen = isDrawerOpenForUri(uri);
      state.drawerTab = getDrawerTabForUri(uri);
    },
    setSearchQuery(state, action: PayloadAction<{ type: 'parents' | 'children'; query: string }>) {
      state.searchQuery[action.payload.type] = action.payload.query;
    },
    setActivePlaylist: (
      state,
      action: PayloadAction<Array<{ url: string; commentText: string }>>,
    ) => {
      state.activePlaylist = action.payload;
    },
    setActivePlaylistTitle: (state, action: PayloadAction<string>) => {
      state.activePlaylistTitle = action.payload;
    },
    setActivePlaylistDescription: (state, action: PayloadAction<string>) => {
      state.activePlaylistDescription = action.payload;
    },
    updateViewSettings: (state, action: PayloadAction<{ view: string; settings: any }>) => {
      const { view, settings } = action.payload;
      state.mapControls.viewSettings[view] = {
        ...state.mapControls.viewSettings[view],
        ...settings,
      };
    },
    // ✅ New atomic action for show_all_relatives mode
    enableShowAllRelatives: (
      state, 
      action: PayloadAction<{
        relatedMaps: Array<{ mapUuid: string; visible: boolean }>;
        dependencies: Array<{ depId: string; visible: boolean }>;
      }>
    ) => {
      const { relatedMaps, dependencies } = action.payload;
      
      // Atomically update all state needed for show_all_relatives mode
      state.mapControls.relatives.objectiveDisplayMode = 'show_all_relatives';
      
      // Enable all related maps
      state.mapControls.relatives.relatedToVisibleMaps = relatedMaps.map(map => ({
        ...map,
        visible: true,
      }));
      
      // Enable all dependencies
      state.mapControls.relatives.visibleMapsDependencies = dependencies.map(dep => ({
        ...dep,
        visible: true,
      }));
    },
    /**
     * Toggle-style setter for the highlighted human UUID.
     * - If dispatched with the same uuid that's already set, clears to null (toggle off)
     * - If dispatched with a different uuid, sets it (switch highlight)
     * - If dispatched with null, clears (explicit clear)
     */
    setHighlightedHumanUuid: (state, action: PayloadAction<string | null>) => {
      if (action.payload === null) {
        state.highlightedHumanUuid = null;
      } else if (state.highlightedHumanUuid === action.payload) {
        state.highlightedHumanUuid = null;
      } else {
        state.highlightedHumanUuid = action.payload;
      }
    },
    setHighlightedObjective(state, action: PayloadAction<string | null>) {
      if (state.highlightedObjectiveUuid === action.payload) return;
      state.highlightedObjectiveUuid = action.payload;
    },
    /**
     * Sets the highlighted interval ending for the Status tab crosshairs effect.
     * - Dispatched with a forIntervalEnding string to highlight that column
     * - Dispatched with null to clear the highlight
     */
    setHighlightedIntervalEnding(state, action: PayloadAction<string | null>) {
      if (state.highlightedIntervalEnding === action.payload) return;
      state.highlightedIntervalEnding = action.payload;
    },
    /**
     * Sets the highlighted result UUID.
     * - Dispatched with a UUID string to highlight that result circle on the map
     * - Dispatched with null to clear the highlight
     * Set when user activates highlight while viewing a result in the drawer.
     */
    setHighlightedResultUuid(state, action: PayloadAction<string | null>) {
      if (state.highlightedResultUuid === action.payload) return;
      state.highlightedResultUuid = action.payload;
    },
  },
});

function isDrawerOpenForUri(uri: string): boolean {
  const isOpen = uri.includes('/results') || uri.includes('/comments');
  return isOpen;
}

function getDrawerTabForUri(uri: string): string {
  const tab = uri.includes('/results') ? 'results' : 'comments';
  return tab;
}

export const {
  setTreeView,
  setSidebarTreeView,
  setCrosshairs,
  setRelativesView,
  setImplementationPlansView,
  setSettingsView,
  setPlaylistMode,
  setHierarchyZoomMode,
  setHelpDialog,
  setShowStatusCodes,
  setAddExistingObjectives,
  setStatusCellDisplay,
  setZoomLevel,
  resetMapZoomLevel,
  setRelativesViewMode,
  setObjectiveDisplayMode,
  setAddedMaps,
  setRelatedToVisibleMaps,
  setVisibleMapsDependencies,
  toggleMapDependencies,
  enableShowAllRelatives,
  setHighlightedHumanUuid,
  toggleDrawer,
  insertPlaceholderRow,
  removePlaceholderRow,
  setUri,
  setSearchQuery,
  setPlaylistDialogOpen,
  setActivePlaylist,
  setActivePlaylistTitle,
  setActivePlaylistDescription,
  updateViewSettings,
  setHighlightedObjective,
  setHighlightedIntervalEnding,
  setHighlightedResultUuid,
} = map3UISlice.actions;

export default map3UISlice.reducer;
